import { getPagePath } from "../../pages/pageRegistry.ts";
import { ParamType, type InputParam } from "../../types/grid.ts";

export interface HyperlinkAction {
  kind: "navigate" | "popup";
  path: string;
}

/** Append query params to a path, using ? or & as appropriate. */
function appendQuery(basePath: string, params: Record<string, string>): string {
  const sep = basePath.includes("?") ? "&" : "?";
  const qs = Object.entries(params).map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join("&");
  return `${basePath}${sep}${qs}`;
}

/** Case-insensitive column index lookup, stripping brackets. */
function findColumnIndex(name: string, colIndex: Record<string, number>): number {
  if (name in colIndex) return colIndex[name]!;
  const lower = name.toLowerCase().replace(/^\[|\]$/g, "");
  for (const [key, idx] of Object.entries(colIndex)) {
    if (key.toLowerCase().replace(/^\[|\]$/g, "") === lower) return idx;
  }
  return -1;
}

/** Get row value by column name. */
function col(name: string, rowData: unknown[], colIndex: Record<string, number>): unknown {
  const idx = findColumnIndex(name, colIndex);
  return idx >= 0 ? rowData[idx] : undefined;
}

/** Get row value by LinkView — resolves the first plain column reference. */
export function colFromLinkView(linkView: string, rowData: unknown[], colIndex: Record<string, number>): unknown {
  if (!linkView) return undefined;
  for (const part of linkView.split(",")) {
    if (part.startsWith("@")) continue;
    const name = part.startsWith("#") ? part.substring(1) : part;
    const idx = findColumnIndex(name, colIndex);
    if (idx >= 0 && rowData[idx] != null) return rowData[idx];
  }
  return undefined;
}

/** LinkView as a key/value map — port of WebAdmin2 HyperLinkPageParam_StandardArgs.
 *  `@key=value` is a literal, a plain name is that column's value in the row;
 *  keys are upper-cased. `#name` entries are ignored, as in WebAdmin2. */
export function linkViewArgs(linkView: string, rowData: unknown[], colIndex: Record<string, number>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  if (!linkView) return out;
  for (const raw of linkView.split(",")) {
    const item = raw.trim();
    if (!item || item.startsWith("#")) continue;
    if (item.startsWith("@")) {
      const eq = item.indexOf("=");
      if (eq > 1) out[item.substring(1, eq).toUpperCase()] = item.substring(eq + 1);
    } else {
      out[item.toUpperCase()] = col(item, rowData, colIndex);
    }
  }
  return out;
}

/**
 * Resolves a hyperlink click into a navigation action.
 * Mirrors WebAdmin2 grid/columnvalue.ts HyperLinkParam_Click + HyperLinkPageParam_Click.
 */
export function resolveHyperlinkAction(
  linkId: number,
  linkView: string,
  _displayType: string,
  value: unknown,
  rowData: unknown[],
  colIndex: Record<string, number>,
): HyperlinkAction | null {
  // WebAdmin2 passes escape(value) then unescapes — we receive raw value directly
  const rawValue = String(value ?? "");

  switch (linkId) {
    // ── Customer ──────────────────────────────────────
    case 9:
    case 24: {
      // WebAdmin2: row["data" + ColumnIndex(attributes, linkView)]
      const id = colFromLinkView(linkView, rowData, colIndex) ?? rawValue;
      return { kind: "popup", path: appendQuery(getPagePath("editcustomer"), { edit: String(id) }) };
    }

    // ── Product (from HyperLinkParam_Click) ──────────
    case 18: {
      // WebAdmin2: hardcoded column lookups, ignores value
      const data = col("ProductID", rowData, colIndex)
        ?? col("Product_ID", rowData, colIndex)
        ?? col("iProductID", rowData, colIndex);
      return { kind: "popup", path: appendQuery(getPagePath("searchEditProduct"), { edit: String(data ?? "") }) };
    }

    // ── Kiosk ─────────────────────────────────────────
    case 50:
      // WebAdmin2: parseInt(value)
      return { kind: "popup", path: appendQuery(getPagePath("editKiosk"), { edit: String(parseInt(rawValue) || rawValue) }) };

    // ── Transaction ───────────────────────────────────
    case 51: {
      // WebAdmin2: parseInt(value), plus iRFID from row
      const discId = col("iRFID", rowData, colIndex);
      const params: Record<string, string> = { transactionId: String(parseInt(rawValue) || rawValue) };
      if (discId != null) params.DiscID = String(discId);
      return { kind: "popup", path: appendQuery(getPagePath("transactiondetails"), params) };
    }

    // ── Location ──────────────────────────────────────
    case 54:
      // WebAdmin2: parseInt(value)
      return { kind: "popup", path: appendQuery(getPagePath("editLocation"), { edit: String(parseInt(rawValue) || rawValue) }) };

    // ── Product (from HyperLinkParam_Click case 214) ─
    case 214: {
      // WebAdmin2: if value contains #, look up iProductID column
      if (rawValue.includes("#")) {
        const productId = col("iProductID", rowData, colIndex);
        if (productId != null) {
          return { kind: "popup", path: appendQuery(getPagePath("searchEditProduct"), { edit: String(productId) }) };
        }
      }
      return { kind: "popup", path: appendQuery(getPagePath("searchEditProduct"), { edit: rawValue }) };
    }

    // ── Fraud stats (popup with filters) ──────────────
    case 206:
      return { kind: "navigate", path: appendQuery(getPagePath("fraudStatisticsDetail2"), { sCardFirstSix: rawValue }) };

    // ── Disc edit ─────────────────────────────────────
    case 207: {
      const discID = col("iRFID", rowData, colIndex);
      const transID = col("iTransactionID", rowData, colIndex);
      const userID = col("iUserID", rowData, colIndex);
      const params: Record<string, string> = {};
      if (discID != null) params.DiscID = String(discID);
      if (transID != null) params.TransactionID = String(transID);
      if (userID != null) params.UserID = String(userID);
      return { kind: "navigate", path: appendQuery(getPagePath("EditDisc"), params) };
    }

    // ── HyperLinkPageParam cases ──────────────────────
    case 3: {
      // Lost discs report (source: fullLifeCycleInventory). LinkView is
      // "<titleColumn>,@iCurrentStatus=N": the first plain token is the title column, the
      // @-token carries the status. Resolve both from the LinkView — fully metadata-driven,
      // no positional/hardcoded column read.
      const parts = linkView.split(",");
      const statusPart = parts.find((p) => p.trim().startsWith("@"))?.split("=")[1] ?? "";
      const title = colFromLinkView(linkView, rowData, colIndex) ?? "";
      return { kind: "navigate", path: appendQuery(getPagePath("lostDiscsReport"), { iCurrentStatus: statusPart, sTitleName: String(title) }) };
    }

    case 4:
      // Promotion → PromotionModal via PagePopup (open the editor in a modal
      // over the current grid instead of navigating away to the promotion page).
      return { kind: "popup", path: appendQuery(getPagePath("searcheditpromotion"), { edit: rawValue }) };

    case 7:
      // Promotion code → EditCodeModal via PagePopup
      return { kind: "popup", path: appendQuery(getPagePath("generateCodes"), { edit: rawValue }) };

    case 8: {
      // Kiosk Version count → the kiosks on that version (WebAdmin2 columnvalue.ts
      // case 8: GridPopup "kioskVersionDetails" filtered on the row's model/version).
      // Filters travel as JSON. The summary views count only live kiosks
      // (iFilterStatus = 1) grouped by pushed version too, so the popup filters
      // the same way or it lists more kiosks than the count. The server turns
      // Equal with null into "= NULL", which matches nothing, so null is IsNull.
      const a = linkViewArgs(linkView, rowData, colIndex);
      const eq = (sColumn: string, v: unknown): InputParam =>
        v == null ? { sColumn, Type: ParamType.IsNull, oValue: null } : { sColumn, Type: ParamType.Equal, oValue: v };
      const filters: InputParam[] = [
        eq("iCapacity", a.ICAPACITY),
        eq("sComputerModel", a.SCOMPUTERMODEL),
        eq("sInstalledVersion", a.SVERSION),
        { sColumn: "iFilterStatus", Type: ParamType.Equal, oValue: 1 },
      ];
      if (findColumnIndex("sPushedVersion", colIndex) >= 0) {
        filters.push(eq("sPushedVersion", col("sPushedVersion", rowData, colIndex)));
      }
      if (a.IOWNEROPERATORID !== undefined) {
        filters.push({ sColumn: "iOwneroperatorID", Type: ParamType.Equal, oValue: a.IOWNEROPERATORID });
      }
      return {
        kind: "popup",
        path: appendQuery("grid", { name: "kioskVersionDetails", title: "Version details", filters: JSON.stringify(filters) }),
      };
    }

    case 233: {
      // Related product
      const relId = col("RelatedProductID", rowData, colIndex);
      return { kind: "navigate", path: appendQuery(getPagePath("searchEditProduct"), { edit: String(relId ?? "") }) };
    }

    // ── Statement invoice info (statementAggregate Invoice Number) ────
    case 199: {
      // Prefer the deterministic InvoiceID column; fall back to LinkView, then value.
      const id = col("InvoiceID", rowData, colIndex)
        ?? colFromLinkView(linkView, rowData, colIndex)
        ?? rawValue;
      if (id == null || String(id) === "") return null;
      // Popup-only target (not in pageRegistry) — PagePopup routes by last path segment.
      return { kind: "popup", path: `statementinvoiceinfo?id=${encodeURIComponent(String(id))}` };
    }

    // Planogram editor links from viewplanogramhistory. LinkID 252 sits on the ConfigID
    // column, 253 on ActiveConfig — both hold the planogram config id (note both columns'
    // LinkView is "#ConfigID", so colFromLinkView can't distinguish them). Read the right
    // column by name; legacy positional index kept as fallback.
    case 252:
      return { kind: "popup", path: appendQuery(getPagePath("viewplanograms"), { edit: String(col("ConfigID", rowData, colIndex) ?? rowData[1] ?? "") }) };

    case 253:
      return { kind: "popup", path: appendQuery(getPagePath("viewplanograms"), { edit: String(col("ActiveConfig", rowData, colIndex) ?? rowData[3] ?? "") }) };

    case 260: {
      const id = col("ID", rowData, colIndex) ?? rawValue;
      return { kind: "navigate", path: appendQuery(getPagePath("blueprintdetail"), { edit: String(id) }) };
    }

    default:
      console.warn(`Unsupported hyperlink LinkID: ${linkId}`);
      return null;
  }
}
